// Arquivo: src/components/LinkTaskModal.js
// O modal usa ReactDOM.createPortal para renderizar direto no document.body,
// escapando do transform/stacking context do .chat-main-window.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import api from '../services/api';
import { FaTasks, FaWrench, FaSearch, FaTimes, FaSpinner, FaLink } from 'react-icons/fa';
import './LinkTaskModal.css';

function useDebounce(value, delay = 400) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

const ResultCard = ({ item, onLink }) => {
    const isTask = item.tipo === 'Tarefa';
    return (
        <button
            className={`lm-result-card ${isTask ? 'task' : 'os'}`}
            onClick={() => onLink(item)}
            type="button"
        >
            <div className={`lm-result-icon ${isTask ? 'task' : 'os'}`}>
                {isTask ? <FaTasks /> : <FaWrench />}
            </div>
            <div className="lm-result-info">
                <span className="lm-result-title">{item.titulo}</span>
                <span className={`lm-result-badge ${isTask ? 'task' : 'os'}`}>
                    {isTask ? 'Tarefa' : 'Ordem de Serviço'}
                </span>
            </div>
            <FaLink className="lm-result-link-icon" />
        </button>
    );
};

// ─── Conteúdo do modal ────────────────────────────────────────

function ModalContent({ onClose, onLink }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [results,    setResults]    = useState([]);
    const [isLoading,  setIsLoading]  = useState(false);
    const [error,      setError]      = useState(null);

    const inputRef      = useRef(null);
    const debouncedTerm = useDebounce(searchTerm, 400);

    useEffect(() => { inputRef.current?.focus(); }, []);

    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const fetchResults = useCallback(async (query) => {
        if (!query || query.trim().length < 2) {
            setResults([]);
            setError(null);
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            // Sem localStorage manual — o interceptor do `api` já injeta o auth token.
            // O tenantMiddleware no backend filtra automaticamente por tenant.
            const { data } = await api.get('/api/search-linkables', {
                params: { query: query.trim() }
            });
            setResults(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('[LinkTaskModal] Erro na busca:', err);
            setError('Não foi possível realizar a busca. Tente novamente.');
            setResults([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchResults(debouncedTerm); }, [debouncedTerm, fetchResults]);

    const renderBody = () => {
        if (isLoading) {
            return (
                <div className="lm-state-container">
                    <FaSpinner className="lm-spinner" />
                    <span>Buscando...</span>
                </div>
            );
        }
        if (error) {
            return (
                <div className="lm-state-container error">
                    <span>{error}</span>
                    <button className="lm-retry-btn" onClick={() => fetchResults(debouncedTerm)}>
                        Tentar novamente
                    </button>
                </div>
            );
        }
        if (searchTerm.trim().length < 2) {
            return (
                <div className="lm-state-container hint">
                    <FaSearch className="lm-hint-icon" />
                    <p>Digite pelo menos <strong>2 caracteres</strong> para buscar Ordens de Serviço ou Tarefas.</p>
                </div>
            );
        }
        if (results.length === 0) {
            return (
                <div className="lm-state-container empty">
                    <p>Nenhum resultado para <strong>"{searchTerm}"</strong>.</p>
                </div>
            );
        }
        return (
            <div className="lm-results-list">
                {results.map(item => (
                    <ResultCard key={item.id} item={item} onLink={onLink} />
                ))}
            </div>
        );
    };

    return (
        <div
            className="lm-overlay"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="Vincular item ao chat"
        >
            <div className="lm-modal" onClick={e => e.stopPropagation()}>
                <div className="lm-header">
                    <div className="lm-header-icon"><FaLink /></div>
                    <h3>Vincular ao Chat</h3>
                    <button className="lm-close-btn" onClick={onClose} title="Fechar (Esc)" aria-label="Fechar">
                        <FaTimes />
                    </button>
                </div>

                <div className="lm-search-wrapper">
                    <FaSearch className="lm-search-icon" />
                    <input
                        ref={inputRef}
                        type="text"
                        className="lm-search-input"
                        placeholder="Buscar por ID ou título..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        autoComplete="off"
                        aria-label="Campo de busca"
                    />
                    {isLoading && <FaSpinner className="lm-input-spinner" />}
                    {searchTerm && !isLoading && (
                        <button className="lm-clear-btn" onClick={() => setSearchTerm('')} type="button" aria-label="Limpar">
                            <FaTimes />
                        </button>
                    )}
                </div>

                <div className="lm-body">{renderBody()}</div>
            </div>
        </div>
    );
}

// ─── Export com Portal ────────────────────────────────────────
// Renderiza fora da árvore do ChatWidget (direto no document.body).
// Resolve o bug onde transform: scale() no pai prende position: fixed filhos.

export default function LinkTaskModal({ onClose, onLink }) {
    return ReactDOM.createPortal(
        <ModalContent onClose={onClose} onLink={onLink} />,
        document.body
    );
}